<template>
  <div class="bg-white rounded-lg shadow-lg p-6">
    <h3 class="text-xl font-semibold mb-4">Análisis de Perfil de Elevación</h3>
    
    <canvas
      ref="canvas"
      class="w-full h-[200px] bg-white border border-gray-200 rounded-lg mb-4"
    ></canvas>
    
    <div v-if="camera" class="space-y-4">
      <!-- Alert when target is out of FOV -->
      <div 
        v-if="!isTargetInFOV"
        class="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg"
      >
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div class="ml-3">
            <p class="text-sm text-red-700">
              El punto objetivo está fuera del campo de visión
            </p>
          </div>
        </div>
      </div>

      <div class="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
        <p><strong>Distancia:</strong> {{ distance.toFixed(1) }} metros</p>
        <p><strong>Altura de la cámara:</strong> {{ camera.height }} metros</p>
        <p><strong>Altura del objetivo:</strong> {{ settings.targetHeight }} metros</p>
        <p><strong>Inclinación:</strong> {{ camera.tilt }}°</p>
        <p><strong>Campo de visión vertical:</strong> {{ verticalFov.toFixed(1) }}°</p>
      </div>
    </div>
    
    <p v-else class="text-gray-500 text-center py-4">
      Selecciona una cámara para ver el análisis de elevación.
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount, computed } from 'vue';
import type { Camera, CameraSettings } from '../types';

const props = defineProps<{
  camera: Camera | null;
  settings: CameraSettings;
}>();

const canvas = ref<HTMLCanvasElement | null>(null);
let resizeObserver: ResizeObserver | null = null;

// Calculate vertical FOV (now directly proportional to horizontal FOV)
const verticalFov = computed(() => {
  if (!props.camera) return 0;
  return props.camera.fov * 0.75; // Typical vertical FOV is about 75% of horizontal FOV
});

// Calculate if target is within FOV
const isTargetInFOV = computed(() => {
  if (!props.camera) return false;
  
  // Calculate angle to target relative to horizontal plane
  const deltaHeight = props.settings.targetHeight - props.camera.height;
  const angleToTarget = Math.atan2(deltaHeight, distance.value) * 180 / Math.PI;
  
  // Calculate FOV boundaries relative to optical axis (tilt)
  const halfFOV = verticalFov.value / 2;
  const upperBound = props.camera.tilt + halfFOV;
  const lowerBound = props.camera.tilt - halfFOV;
  
  return angleToTarget >= lowerBound && angleToTarget <= upperBound;
});

// Calculate distance between camera and target
const distance = computed(() => {
  if (!props.camera) return 0;
  
  const lat1 = props.camera.position[0];
  const lon1 = props.camera.position[1];
  const lat2 = props.camera.target[0];
  const lon2 = props.camera.target[1];
  
  const R = 6371000; // Earth's radius in meters
  const φ1 = lat1 * Math.PI/180;
  const φ2 = lat2 * Math.PI/180;
  const Δφ = (lat2-lat1) * Math.PI/180;
  const Δλ = (lon2-lon1) * Math.PI/180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  
  return R * c;
});

function drawProfile() {
  if (!canvas.value || !props.camera) return;
  
  const ctx = canvas.value.getContext('2d');
  if (!ctx) return;

  const width = canvas.value.width;
  const height = canvas.value.height;
  
  // Clear canvas
  ctx.clearRect(0, 0, width, height);
  
  // Calculate padding and usable area
  const padding = { left: 60, right: 30, top: 20, bottom: 40 };
  const usableWidth = width - padding.left - padding.right;
  const usableHeight = height - padding.top - padding.bottom;
  
  // Calculate max height for scaling
  const maxHeight = Math.max(
    Math.abs(props.camera.height),
    Math.abs(props.settings.targetHeight)
  ) * 1.5;
  
  // Calculate scales
  const scaleY = usableHeight / (maxHeight * 2);
  const scaleX = usableWidth / distance.value;
  
  // Transform coordinate functions
  const transformY = (y: number) => height - padding.bottom - ((y + maxHeight) * scaleY);
  const transformX = (x: number) => padding.left + (x * scaleX);
  
  // Draw grid and zero line
  ctx.beginPath();
  ctx.strokeStyle = '#f0f0f0';
  ctx.lineWidth = 1;
  
  // Horizontal grid lines
  for (let y = -maxHeight; y <= maxHeight; y += maxHeight / 5) {
    const py = transformY(y);
    ctx.moveTo(padding.left, py);
    ctx.lineTo(width - padding.right, py);
  }
  ctx.stroke();
  
  // Draw zero line (ground level)
  ctx.beginPath();
  ctx.strokeStyle = '#999';
  ctx.lineWidth = 1;
  ctx.setLineDash([5, 5]);
  const zeroY = transformY(0);
  ctx.moveTo(padding.left, zeroY);
  ctx.lineTo(width - padding.right, zeroY);
  ctx.stroke();
  ctx.setLineDash([]);
  
  // Draw axes
  ctx.beginPath();
  ctx.strokeStyle = '#999';
  ctx.lineWidth = 2;
  // Y axis
  ctx.moveTo(padding.left, height - padding.bottom);
  ctx.lineTo(padding.left, padding.top);
  // X axis
  ctx.moveTo(padding.left, height - padding.bottom);
  ctx.lineTo(width - padding.right, height - padding.bottom);
  ctx.stroke();
  
  // Draw camera position
  const cameraX = transformX(0);
  const cameraY = transformY(props.camera.height);
  ctx.beginPath();
  ctx.fillStyle = '#18223B';
  ctx.arc(cameraX, cameraY, 5, 0, Math.PI * 2);
  ctx.fill();
  
  // Draw target position
  const targetX = transformX(distance.value);
  const targetY = transformY(props.settings.targetHeight);
  ctx.beginPath();
  ctx.fillStyle = isTargetInFOV.value ? '#F6A11B' : '#ef4444';
  ctx.arc(targetX, targetY, 5, 0, Math.PI * 2);
  ctx.fill();
  
  // Draw line from target to ground
  ctx.beginPath();
  ctx.strokeStyle = isTargetInFOV.value ? '#F6A11B' : '#ef4444';
  ctx.lineWidth = 2;
  ctx.setLineDash([5, 5]);
  ctx.moveTo(targetX, targetY);
  ctx.lineTo(targetX, transformY(0));
  ctx.stroke();
  ctx.setLineDash([]);
  
  // Draw FOV cone
  const tiltRad = props.camera.tilt * Math.PI / 180;
  const halfFovRad = (verticalFov.value / 2) * Math.PI / 180;
  
  ctx.beginPath();
  ctx.fillStyle = 'rgba(24, 34, 59, 0.1)';
  ctx.strokeStyle = '#18223B';
  ctx.lineWidth = 1;
  
  // Start at camera position
  ctx.moveTo(cameraX, cameraY);
  
  // Calculate FOV boundaries
  const maxX = distance.value * 1.2; // Extend slightly beyond target
  
  // Draw upper FOV line
  const upperAngle = tiltRad + halfFovRad;
  const upperY = (x: number) => props.camera!.height + Math.tan(upperAngle) * x;
  
  for (let x = 0; x <= maxX; x += maxX/100) {
    ctx.lineTo(transformX(x), transformY(upperY(x)));
  }
  
  // Draw lower FOV line (back to camera)
  const lowerAngle = tiltRad - halfFovRad;
  const lowerY = (x: number) => props.camera!.height + Math.tan(lowerAngle) * x;
  
  for (let x = maxX; x >= 0; x -= maxX/100) {
    ctx.lineTo(transformX(x), transformY(lowerY(x)));
  }
  
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  
  // Draw optical axis (bisector)
  ctx.beginPath();
  ctx.strokeStyle = '#18223B';
  ctx.setLineDash([5, 5]);
  ctx.moveTo(cameraX, cameraY);
  const opticalAxisY = (x: number) => props.camera!.height + Math.tan(tiltRad) * x;
  ctx.lineTo(transformX(maxX), transformY(opticalAxisY(maxX)));
  ctx.stroke();
  ctx.setLineDash([]);
  
  // Draw labels
  ctx.fillStyle = '#666';
  ctx.font = '12px Arial';
  
  // Height labels
  ctx.textAlign = 'right';
  for (let y = -maxHeight; y <= maxHeight; y += maxHeight / 5) {
    const py = transformY(y);
    ctx.fillText(`${y.toFixed(1)}m`, padding.left - 5, py + 4);
  }
  
  // Distance labels
  ctx.textAlign = 'center';
  for (let x = 0; x <= distance.value; x += distance.value / 4) {
    const px = transformX(x);
    ctx.fillText(`${x.toFixed(1)}m`, px, height - padding.bottom + 20);
  }
  
  // Angle labels
  ctx.textAlign = 'left';
  ctx.fillText(
    `Inclinación: ${props.camera.tilt}°`,
    cameraX + 10,
    cameraY - 25
  );
  ctx.fillText(
    `FOV: ${verticalFov.value.toFixed(1)}°`,
    cameraX + 10,
    cameraY - 10
  );
}

// Watch for any changes that should trigger a redraw
watch([
  () => props.camera,
  () => props.settings,
  () => verticalFov.value,
  () => distance.value,
  () => isTargetInFOV.value
], () => {
  requestAnimationFrame(() => {
    drawProfile();
  });
}, { deep: true });

// Handle window resize
onMounted(() => {
  if (canvas.value) {
    const updateCanvasSize = () => {
      if (canvas.value) {
        canvas.value.width = canvas.value.offsetWidth;
        canvas.value.height = canvas.value.offsetHeight;
        drawProfile();
      }
    };
    
    resizeObserver = new ResizeObserver(updateCanvasSize);
    resizeObserver.observe(canvas.value);
    
    // Initial draw
    updateCanvasSize();
  }
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
});
</script>